<template>
  <modal :errors="errors" v-bind="$attrs" v-on="$listeners">
    <template v-slot:title>
      <h1>
        {{
          isOrganizationUseDummyEmail
            ? $t("organizations.users_section.modals.invite_users.generate_dummy_learner")
            : $t("organizations.users_section.modals.invite_users.title_single")
        }}
      </h1>
    </template>
    <template v-slot:body>
      <el-form ref="form" :model="form" :rules="rules" class="auth-form">
        <div class="form-row">
          <elsa-input
            v-model.lazy="form.first_name"
            :label="$t('organization_profile.invite_admin_modal.first_name')"
            :placeholder="$t('organization_profile.invite_admin_modal.first_name')"
            class="first-name"
            name="first_name"
          >
            {{ $t("organization_profile.edit_organization_modal.city_field.label") }}
          </elsa-input>
          <elsa-input
            v-model.lazy="form.last_name"
            :label="$t('organization_profile.invite_admin_modal.last_name')"
            :placeholder="$t('organization_profile.invite_admin_modal.last_name')"
            class="last-name"
            name="last_name"
          >
            {{ $t("organization_profile.edit_organization_modal.state_field.label") }}
          </elsa-input>
        </div>
        <elsa-input
          v-if="isOrganizationUseDummyEmail"
          v-model.trim.lazy="dummyEmail"
          :disabled="isOrganizationUseDummyEmail"
          :label="$t('dummy_learner.dummy_label')"
          :placeholder="$t('organization_profile.invite_admin_modal.email_field.placeholder')"
          name="email"
        >
          {{ $t("dummy_learner.dummy_label") }}
        </elsa-input>
        <elsa-input
          v-else
          v-model.trim.lazy="form.email"
          :label="$t('organization_profile.invite_admin_modal.email_field.label')"
          :placeholder="$t('organization_profile.invite_admin_modal.email_field.placeholder')"
          name="email"
        >
          {{ $t("organization_profile.invite_admin_modal.email_field.label") }}
        </elsa-input>
        <div class="form-row">
          <elsa-select
            v-if="isElsaSpeakProduct"
            v-model.lazy="form.selectedTeam"
            :label="$t($i18nLabelsHelper(currentOrganizationType, 'modals.invite_users.assign_group'))"
            :no-data-text="$t($i18nLabelsHelper(currentOrganizationType, 'modals.invite_users.no_group_available'))"
            :options="classes"
            :placeholder="
              $t($i18nLabelsHelper(currentOrganizationType, 'modals.invite_users.select_group_placeholder'))
            "
            class="select-team"
            filterable
            name="selectedTeam"
          />
          <elsa-select
            v-model.lazy="form.selectedMembership"
            :label="$t('organizations.users_section.modals.invite_users.assign_elsa_access')"
            :options="availableMemberships"
            class="select-membership"
            name="selectedMembership"
            no-data-text="No plan available"
          />
        </div>
        <div v-if="!isOrganizationUseDummyEmail" class="form-row">
          <el-form-item class="consent" prop="consent" show-message>
            <el-checkbox v-model.lazy="form.consent" class="elsa-checkbox" label="consent">
              {{
                $t("organizations.users_section.modals.invite_users.consent1", {
                  organizationName: organizationName,
                })
              }}
            </el-checkbox>
          </el-form-item>
        </div>
      </el-form>
      <el-button class="swap-mode-btn" type="text" @click.native="changeToMultipleMode">
        <i class="el-icon-circle-plus-outline" />
        {{ $t("organizations.users_section.modals.invite_users.add_multiple_learners") }}
      </el-button>
    </template>
    <template v-slot:footer>
      <span class="dialog-footer">
        <tertiary-button @click.native="handleCancelClick">
          {{ $t("organization_profile.invite_admin_modal.cancel_button") }}
        </tertiary-button>
        <primary-button
          ref="submit"
          v-loading.fullscreen.lock="fullscreenLoading"
          :disabled="!isOrganizationUseDummyEmail"
          type="submit"
          @click.native="handleInvite"
        >
          {{
            isOrganizationUseDummyEmail
              ? $t("organization_profile.invite_admin_modal.create_button")
              : $t("organization_profile.invite_admin_modal.invite_button")
          }}
        </primary-button>
      </span>
    </template>
  </modal>
</template>

<script>
import Modal from "@/components/common/Modal";
import PrimaryButton from "@/components/common/buttons/PrimaryButton";
import TertiaryButton from "@/components/common/buttons/TertiaryButton";
import ElsaInput from "@/components/common/Input";
import ElsaSelect from "@/components/common/Select";
import { handleFormValidation, handleSubmitButtonState, isPropEmpty } from "@/helpers/formValidationHelper";
import axios from "@/utils/axios";
import { compose, defaultTo, isNil, of, path, pathOr, reject } from "ramda";
import { successMessage, warningMessage } from "@/utils/messages";
import { mapActions, mapGetters } from "vuex";
import { PLAN_MONTH_DURATION } from "@/constants/generic";
import { addMonths } from "date-fns";
import { formatDate } from "@/utils/formatDate";
import { getPlanLabel } from "@/helpers/functions";

export default {
  components: {
    Modal,
    PrimaryButton,
    TertiaryButton,
    ElsaInput,
    ElsaSelect,
  },
  props: {
    organizationId: {
      type: Number,
    },
    inviteMultipleMode: {
      default: null,
    },
    isOrganizationUseDummyEmail: {
      type: Boolean,
    },
  },
  data() {
    return {
      errors: {},
      form: {
        consent: true,
        selectedTeam: this.$route.params.classId ? parseInt(this.$route.params.classId) : undefined,
      },
      rules: {
        email: [
          {
            type: "email",
            message: this.$t("organizations.users_section.modals.invite_learners.email_validate_msg"),
            trigger: "blur",
            required: true,
          },
        ],
        first_name: [
          {
            type: "string",
            message: this.$t("organizations.users_section.modals.invite_learners.first_name_validation_msg"),
            trigger: "blur",
            required: true,
          },
        ],
        last_name: [
          {
            type: "string",
            message: this.$t("organizations.users_section.modals.invite_learners.last_name_validation_msg"),
            trigger: "blur",
            required: true,
          },
        ],
      },
      fullscreenLoading: false,
      orgMembership: [],
    };
  },
  methods: {
    handleInvite(e) {
      e.preventDefault();
      handleFormValidation(this.$refs.form, this.inviteUsers, this.handleError);
    },
    async inviteUsers() {
      this.fullscreenLoading = true;
      const role = "student";
      const emails = this.isOrganizationUseDummyEmail
        ? [this.currentOrganization.code ? this.currentOrganization.code + "@k12.elsanow.io" : "ELSA@k12.elsanow.io"]
        : compose(of, pathOr({}, ["form", "email"]))(this);
      const first_names = compose(of, pathOr({}, ["form", "first_name"]))(this);
      const last_names = compose(of, pathOr({}, ["form", "last_name"]))(this);
      const group = pathOr(null, ["form", "selectedTeam"])(this);
      const consent = pathOr(false, ["form", "consent"])(this);
      const organizationId = this.organizationId;
      const selectedMembership = compose(of, pathOr(null, ["form", "selectedMembership"]))(this);

      let plan_ids = [];
      let packages = [];

      selectedMembership.forEach(id => {
        if (id && id.toString().includes("_")) {
          // this is combine of same packages
          id = parseInt(id.toString().split("_")[0]);
        }

        if (id) {
          plan_ids.push(id);
          packages.push(this.orgMembership.find(plan => plan.id === id)["package"]);
        }
      });

      const payload = reject(isNil)({
        organization: organizationId,
        group: group,
        role,
        emails,
        first_names,
        last_names,
        auto_join: consent ? 1 : 0,
        plan_ids: plan_ids,
        packages: packages,
      });

      this.$axios
        .post(this.isOrganizationUseDummyEmail ? "/v2/accounts/invite_learner/" : "/v2/accounts/invite/", payload)
        .then(() => {
          this.fullscreenLoading = false;
          if (this.isOrganizationUseDummyEmail) successMessage(this.$t("dummy_learner.enroll_success"))(this);
          else successMessage(this.$t("organization_profile.invite_admin_modal.invite_success"))(this);
          this.resetModal();
          this.$emit("update-pending");
        });
      try {
        this.$gtag.event(`${document.title}-learner-invite`, {
          category: `Send-Invite`,
          label: `Add Learners`,
        });
      } catch (error) {
        error;
      }
    },
    handleCancelClick() {
      this.resetModal();
    },
    onFormChange() {
      if (this.isOrganizationUseDummyEmail)
        this.form.email = this.currentOrganization.code
          ? this.currentOrganization.code + "@k12.elsanow.io"
          : "ELSA@k12.elsanow.io";
      handleSubmitButtonState(this.$refs.submit.$el, !isPropEmpty("email")(this.form));
    },
    handleError(error) {
      this.errors = error;
    },
    resetModal() {
      this.errors = {};
      this.$refs.form.resetFields();
      handleSubmitButtonState(this.$refs.submit.$el, true);
      this.$emit("closed");
    },
    ...mapActions("classes", ["getAllClasses"]),
    getOrganizationContracts() {
      let url = `v1/checkouts/${this.organizationId}/payments`;
      return axios.get(url).then(response => {
        const payments = response.data;
        if (payments.length > 0) {
          payments.forEach(payment => {
            const plan_details = payment.plan_details;
            plan_details.forEach(plan => {
              const nowTime = new Date().getTime();
              let validTime = null;
              if (plan.auto_active_license_date && plan.is_subscription) {
                validTime = this.expiredAtForAutoActive(plan.auto_active_license_date, plan.package);
              }
              // check remaining license and not expired yet, if payment offline validtime = null
              if (plan.number_of_learner > plan.number_of_license_used && (validTime >= nowTime || !validTime)) {
                const newPlan = {
                  id: plan.id,
                  package: plan.package,
                  expire_date: validTime
                    ? formatDate(validTime, this.currentOrganization.timezone, "MMM dd, yyyy")
                    : null,
                  product_type: plan.product_types[0],
                };
                this.orgMembership.push(newPlan);
              }
            });
          });
          if (this.isNovaOrg) {
            // select first option
            this.form.selectedMembership = this.availableMemberships[0].value;
          }
        }
      });
    },
    changeToMultipleMode() {
      this.$emit("update:inviteMultipleMode", true);
    },
    expiredAtForAutoActive(date_auto_active, membership) {
      const invalidDate = addMonths(new Date(date_auto_active), PLAN_MONTH_DURATION[membership]);
      return invalidDate;
    },
    combineOptions(data) {
      const combinedData = {};

      data.forEach(item => {
        const label = item.label;

        if (label in combinedData) {
          combinedData[label].value += `_${item.value}`;
        } else {
          combinedData[label] = { label: label, value: item.value || "" };
        }
      });

      return Object.values(combinedData);
    },
  },
  computed: {
    sortedOrgPlans() {
      const plans = [...this.orgMembership];
      const sortedData = plans.sort((a, b) => a.id - b.id); // sort by id from earliest to latest
      const data = [];

      sortedData.forEach(p => {
        const expire_date_label = p.expire_date ? " - exp. on " + p.expire_date : "";
        const plan_name = getPlanLabel(p.product_type, p.package);
        const plan_label = p.qty - p.qty_assigned + " " + plan_name + expire_date_label;
        data.push({ ...p, plan_label, plan_name, expire_date_label });
      });

      data.sort((a, b) => {
        if (a.expire_date && !b.expire_date) {
          return -1; // a has expire_date, b does not, so a should come first
        } else if (!a.expire_date && b.expire_date) {
          return 1; // b has expire_date, a does not, so b should come first
        } else if (a.expire_date && b.expire_date) {
          return new Date(a.expire_date) - new Date(b.expire_date); // sort by expire_date from earliest to latest
        } else {
          return a.plan_name.localeCompare(b.plan_name); // sort alphabetically by plan_name
        }
      });

      return data;
    },
    availableMemberships() {
      const avaiMemberships = [];

      this.sortedOrgPlans.forEach(plan => {
        let newPlan = {
          label: plan.plan_name + plan.expire_date_label,
          value: plan.id,
        };

        if (plan.qty <= plan.qty_assigned) {
          newPlan["disabled"] = true;
        }

        avaiMemberships.push(newPlan);
      });

      return this.combineOptions(avaiMemberships);
    },
    organizationName() {
      const organizationName = compose(
        defaultTo(this.$store.getters["organization/currentOrganization"].name),
        path(["organizationData", "name"])
      )(this);

      return organizationName;
    },
    dummyEmail() {
      return this.currentOrganization.code ? this.currentOrganization.code + "@k12.elsanow.io" : "ELSA@k12.elsanow.io";
    },
    ...mapGetters({
      classes: "classes/getAllClasses",
      currentOrganizationType: "organization/currentOrganizationType",
      currentOrganization: "organization/currentOrganization",
      isNovaOrg: "organization/isNovaOrg",
      isElsaSpeakProduct: "organization/isElsaSpeakProduct",
    }),
  },
  mounted() {
    try {
      this.getAllClasses();
      this.getOrganizationContracts();
    } catch {
      return warningMessage(this.$t("common.generic_error_message"))(this);
    }
  },
  watch: {
    form: {
      deep: true,
      handler() {
        return this.onFormChange();
      },
    },
  },
};
</script>

<style lang="scss" scoped>
.elsa-flat-button.el-button {
  padding: 0;
  margin: 0;
}

::v-deep .el-form .el-form-item .el-form-item__label {
  text-transform: capitalize;
}

.invite-desc {
  font-size: 16px;
  line-height: 24px;
  font-weight: normal;
  color: #121131;
  opacity: 0.8;
}

.auth-form {
  .form-row {
    display: flex;

    .first-name,
    .last-name,
    .select-team,
    .select-membership {
      width: 50%;
    }

    .first-name,
    .select-team {
      margin-right: 30px;
    }
  }

  padding-bottom: 25px !important;
}

.swap-mode-btn {
  flex-direction: row !important;
  padding-bottom: 0px !important;
}
</style>
